import { useContext, useEffect, useState } from 'react';
import { useLockBodyScroll, useMedia } from 'react-use';

import * as SG from 'styles';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  setBarColor,
  setLatestSeason,
  setMarketLine,
  setMenu,
  setOrderId,
  setOrderNumber,
  setSeason,
  setWarehouse,
  setZone,
} from 'redux/slices/settings';
import { getUserZonesWithSpecial } from 'redux/slices/userData';

import { usersDataService } from 'services';
import { productDataService } from 'services/products.service';

import { LayoutProvider } from 'contexts';
import { ChatContext } from 'contexts/ChatContext';

import { useNavData } from 'hooks/useNavData';
import { useSettingsData } from 'hooks/useSettingsData';

import { registerCatalogueLabels } from 'helpers/registerCatalogueLabels';

import { Footer } from 'sections/Footer';
import { Header } from 'sections/Header/Header';
import { Nav } from 'sections/Nav';
import { SettingsBar } from 'sections/SettingsBar';

import { Chat } from 'components/Chat';
import { Hamburger } from 'components/Hamburger';
import { Sidebar } from 'components/Sidebar';

import * as S from './DefaultLayout.styles';
import { ScrollButton } from './components/ScrollButton';

export type DefaultLayoutProps = {
  children: React.ReactNode;
  withHeader?: boolean;
  isGridChange?: boolean;
  isNested?: boolean;
  footerHasMargin?: boolean;
};

const defaultProps: Partial<DefaultLayoutProps> = {
  withHeader: false,
  isGridChange: false,
  isNested: false,
  footerHasMargin: false,
};

export function DefaultLayout(props: DefaultLayoutProps): React.ReactElement {
  const { children, withHeader, isNested, footerHasMargin, isGridChange } = {
    ...defaultProps,
    ...props,
  };

  const dispatch = useAppDispatch();

  const { season, marketLine, warehouse, zone, orderNumber, barColor } = useAppSelector(
    (state) => state.settings
  );
  const seasons = useAppSelector((state) => state.userData.user?.season);
  const zones = useAppSelector(getUserZonesWithSpecial);
  const navData = useNavData();

  const { customerData, isVisible, messages } = useContext(ChatContext);

  const [isSidebarOpen, isSidebarOpenSet] = useState(false);
  const [isSettingsOpen, isSettingsOpenSet] = useState(false);

  const isMobile = useMedia('(max-width:1024px)');

  const settingsDataMobile = useSettingsData()
    .map((item) => item.items)
    .flat();

  useEffect(() => {
    if (season && marketLine) {
      productDataService.getMenu(season, marketLine).then((res) => {
        registerCatalogueLabels(res.data);
        dispatch(setMenu(res.data));
      });
    }
  }, [season, marketLine]);

  useEffect(() => {
    if (!seasons) return;
    const lastSeason = seasons[0];
    const barColorFromStorage = localStorage.getItem('barColor');

    if (!season) {
      dispatch(setSeason(localStorage.getItem('season') || lastSeason?.season));
    }

    if (!marketLine) {
      dispatch(
        setMarketLine(
          localStorage.getItem('marketLine') || lastSeason?.descEng.toLocaleLowerCase()
        )
      );
    }

    if (!warehouse.code) {
      dispatch(
        setWarehouse(
          JSON.parse(localStorage.getItem('warehouse')!) || {
            id: '1',
            code: '1',
            name: 'Catalogue',
          }
        )
      );

      if (!barColorFromStorage) {
        localStorage.setItem('barColor', 'black');
        dispatch(setBarColor('black'));
      }
    }

    if (!zone) {
      dispatch(setZone(localStorage.getItem('zone') || zones[0]?.code));
    }

    if (!orderNumber) {
      dispatch(setOrderNumber(localStorage.getItem('orderNumber')) || '');
      dispatch(setOrderId(localStorage.getItem('orderId') || ''));
    }
  }, [seasons]);

  useLockBodyScroll(isSidebarOpen);

  useEffect(() => {
    if (!season) return;

    usersDataService.getLatestSeason().then((res) => {
      dispatch(setLatestSeason(res.data));
    });
  }, [season]);

  useEffect(() => {
    if (barColor) return;

    dispatch(setBarColor(localStorage.getItem('barColor')));
  }, [barColor]);

  return (
    <LayoutProvider>
      {customerData && isVisible ? (
        <Chat
          messages={messages}
          customerId={customerData.id}
          customerName={customerData.companyName}
          customerCode={customerData.code}
        />
      ) : null}

      <S.Layout>
        {isMobile ? (
          <>
            <S.NavMobile>
              <Hamburger
                isOpen={isSidebarOpen}
                isOpenSet={() => {
                  isSettingsOpenSet(false);
                  isSidebarOpenSet(!isSidebarOpen);
                }}
              />

              <S.SettingsButton
                isOpen={isSettingsOpen}
                onClick={() => {
                  isSidebarOpenSet(false);
                  isSettingsOpenSet(!isSettingsOpen);
                }}
                iconLeft="FiSettings"
                scale="xl"
              />
            </S.NavMobile>

            <Sidebar data={navData} type="menu" isOpen={isSidebarOpen} />

            <Sidebar data={settingsDataMobile} type="settings" isOpen={isSettingsOpen} />
          </>
        ) : (
          <>
            <SettingsBar />

            <Nav />
          </>
        )}

        {withHeader && <Header isGridChange={isGridChange} />}

        <S.Container>
          {children && (
            <S.Wrapper>{isNested ? <SG.Inner>{children}</SG.Inner> : children}</S.Wrapper>
          )}
        </S.Container>

        <Footer withMarginTop={footerHasMargin} />

        <ScrollButton />
      </S.Layout>
    </LayoutProvider>
  );
}
